// PLAN-cockpit-v2-iter1 Phase 3: Home Lens
// Overview (Latest / Top Articles / Domain pulse) + Needs review (Stale / Pending suggestions 等)
// iA Writer 系 minimal、Card で囲まず typography と divider で section を分ける。

import { useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import { useV2Router } from '../router'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useRecentlyOpened } from '../hooks/useRecentlyOpened'
import { permalinkHref, itemLinkClick } from '../lib/itemLink'
import type { SearchHit, SearchResponse } from '../../types'

const STORAGE_LAST_VISIT = 'cockpit_v2.last_visit_at'
const ACTIVE_DOMAINS = ['finance', 'health', 'mind', 'relation', 'science-tech', 'general'] as const

function setLastVisit() {
  window.localStorage.setItem(STORAGE_LAST_VISIT, new Date().toISOString())
}

interface DomainPulse {
  domain: string
  count: number
  lastUpdated: string | null
}

interface TopArticle {
  id: string
  title: string
  one_line: string | null
  sourced_count?: number
  has_decision?: number
  x_status?: string | null
}

// PLAN-cockpit-decision-visibility (2026-05-14): 一覧の decision マーカー (compact)
function DecisionMark({ x_status }: { x_status?: string | null }) {
  return (
    <span
      className={`text-[10px] uppercase font-semibold px-1 py-0.5 rounded flex-shrink-0 ${
        x_status === 'all_rejected' ? 'text-text-subtle' : 'text-emerald-600'
      }`}
      style={{
        backgroundColor: x_status === 'all_rejected'
          ? 'color-mix(in srgb, var(--text-subtle) 14%, transparent)'
          : 'color-mix(in srgb, var(--state-success, #10b981) 16%, transparent)',
      }}
      title={`Decision (${x_status ?? 'present'})`}
    >
      ◇
    </span>
  )
}

interface NeedsReview {
  stale: SearchHit[]
  reviewDue: SearchHit[]   // Phase 3-B: review_by 経過済 Article (sort=review_by&limit=20 で取得後クライアントで filter)
  pending: number | null
  deprecated: SearchHit[]
  loading: boolean
}

export function HomeLens() {
  const { navigate } = useV2Router()
  const { setSelectedItem } = useSelectedItem()
  const { items: recentlyOpened } = useRecentlyOpened(5)

  // This Week signals
  const since1w = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const [newEntries, setNewEntries] = useState<number | null>(null)
  const [newArticles, setNewArticles] = useState<number | null>(null)
  const [pendingSuggestions, setPendingSuggestions] = useState<number | null>(null)
  const [signalsLoading, setSignalsLoading] = useState(true)

  // Latest / Top Articles
  const [latestItems, setLatestItems] = useState<SearchHit[]>([])
  const [topArticles, setTopArticles] = useState<TopArticle[]>([])
  const [overviewLoading, setOverviewLoading] = useState(true)

  // Domain pulse
  const [pulse, setPulse] = useState<DomainPulse[]>([])

  // Needs review
  const [needsReview, setNeedsReview] = useState<NeedsReview>({ stale: [], reviewDue: [], pending: null, deprecated: [], loading: true })

  useEffect(() => { return () => setLastVisit() }, [])

  // Signals (This Week)
  useEffect(() => {
    const ac = new AbortController()
    Promise.all([
      apiFetch<SearchResponse>('/api/search', { query: { since: since1w, limit: 1 }, signal: ac.signal }).catch(() => null),
      apiFetch<SearchResponse>('/api/articles/search', { query: { since: since1w, limit: 1 }, signal: ac.signal }).catch(() => null),
      apiFetch<{ suggestions?: unknown[] }>('/api/article-suggestions', { query: { limit: 1 }, signal: ac.signal }).catch(() => null),
    ]).then(([entries, articles, sg]) => {
      setNewEntries(entries?.total ?? null)
      setNewArticles(articles?.total ?? null)
      setPendingSuggestions(sg?.suggestions?.length != null ? (sg as { total?: number }).total ?? 0 : null)
      setSignalsLoading(false)
    }).catch(() => setSignalsLoading(false))
    return () => ac.abort()
  }, [since1w])

  // Overview: Latest items + Top Articles
  useEffect(() => {
    const ac = new AbortController()
    Promise.all([
      apiFetch<SearchResponse>('/api/search', { query: { sort: '-created_at', limit: 10 }, signal: ac.signal }).catch(() => null),
      apiFetch<SearchResponse>('/api/articles/search', { query: { sort: '-sourced_count', limit: 10 }, signal: ac.signal }).catch(() => null),
    ]).then(([latest, tops]) => {
      setLatestItems(latest?.entries ?? [])
      setTopArticles((tops?.entries ?? []).map(e => ({ id: e.id, title: e.title, one_line: e.one_line, sourced_count: e.sourced_count, has_decision: e.has_decision, x_status: e.x_status })))
      setOverviewLoading(false)
    }).catch(() => setOverviewLoading(false))
    return () => ac.abort()
  }, [])

  // Domain pulse (5 domain 並列)
  useEffect(() => {
    const ac = new AbortController()
    Promise.all(
      ACTIVE_DOMAINS.map(d =>
        apiFetch<SearchResponse>('/api/search', { query: { domain: d, domain_mode: 'broad', limit: 1 }, signal: ac.signal })
          .then(r => ({ domain: d as string, count: r.total ?? 0, lastUpdated: r.entries?.[0]?.created_at ?? null }))
          .catch(() => ({ domain: d as string, count: 0, lastUpdated: null }))
      )
    ).then(setPulse)
    return () => ac.abort()
  }, [])

  // Needs review
  useEffect(() => {
    const ac = new AbortController()
    const now = new Date().toISOString()
    Promise.all([
      apiFetch<SearchResponse>('/api/articles/search', { query: { stale: 'true', limit: 5 }, signal: ac.signal }).catch(() => null),
      // Review due: sort=review_by ASC で 20 件取得 → client 側で review_by 経過済のみ抽出 (上位 5)
      apiFetch<SearchResponse>('/api/articles/search', { query: { sort: 'review_by', limit: 20 }, signal: ac.signal }).catch(() => null),
      apiFetch<SearchResponse>('/api/articles/search', { query: { include_stage: 'deprecated', limit: 5 }, signal: ac.signal }).catch(() => null),
      apiFetch<{ suggestions?: unknown[] }>('/api/article-suggestions', { query: { limit: 5 } , signal: ac.signal }).catch(() => null),
    ]).then(([stale, reviewBatch, depr, sg]) => {
      const reviewDue = (reviewBatch?.entries ?? [])
        .filter(h => h.review_by && h.review_by < now)
        .slice(0, 5)
      setNeedsReview({
        stale: stale?.entries ?? [],
        reviewDue,
        deprecated: depr?.entries ?? [],
        pending: sg ? ((sg as { total?: number }).total ?? (sg.suggestions?.length ?? 0)) : null,
        loading: false,
      })
    }).catch(() => setNeedsReview(p => ({ ...p, loading: false })))
    return () => ac.abort()
  }, [])

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4 sm:py-8 space-y-5 sm:space-y-8">
      <header>
        <p className="text-xs uppercase tracking-wide text-text-subtle">{formatToday()}</p>
        <h1 className="text-xl sm:text-2xl font-medium text-text-base mt-0.5">KDB Cockpit</h1>
      </header>

      {/* This Week summary (compact) */}
      <section>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">This Week</h2>
        {signalsLoading ? (
          <p className="text-sm text-text-subtle">Loading…</p>
        ) : (
          <ul className="space-y-1 text-sm text-text-base">
            <li>
              <span className="font-medium">{newEntries ?? 0}</span>{' '}
              <span className="text-text-muted">new entries (last 7d)</span>
            </li>
            <li>
              <span className="font-medium">{newArticles ?? 0}</span>{' '}
              <span className="text-text-muted">new articles</span>
            </li>
            {pendingSuggestions != null && pendingSuggestions > 0 && (
              <li>
                <span className="font-medium">{pendingSuggestions}</span>{' '}
                <span className="text-text-muted">pending suggestions</span>
                <button type="button" onClick={() => navigate('collaborate')} className="ml-2 text-xs text-accent-primary hover:underline">Review →</button>
              </li>
            )}
          </ul>
        )}
      </section>

      {/* Recently opened (self-tracking、localStorage 由来) */}
      {recentlyOpened.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Recently opened</h2>
          <ul className="space-y-1.5">
            {recentlyOpened.map(r => (
              <li key={r.id} className="flex items-start gap-2">
                <span className={`text-[10px] uppercase tracking-wide flex-shrink-0 mt-0.5 ${r.itemType === 'article' ? 'text-accent-secondary' : 'text-text-subtle'}`}>
                  {r.itemType === 'article' ? 'Article' : 'Entry'}
                </span>
                <a href={permalinkHref(r.id)}
                  onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: r.id, itemType: r.itemType }))}
                  className="flex-1 text-sm text-text-base hover:text-accent-primary text-left line-clamp-1">
                  {r.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Latest items (上位に移動) */}
      <section>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Latest</h2>
        {overviewLoading ? (
          <p className="text-sm text-text-subtle">Loading…</p>
        ) : latestItems.length === 0 ? (
          <p className="text-sm text-text-muted">No items yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {latestItems.slice(0, 10).map(hit => {
              const itemType: 'entry' | 'article' = hit.origin === 'synthesized' ? 'article' : 'entry'
              return (
                <li key={hit.id} className="flex items-start gap-2">
                  <span className={`text-[10px] uppercase tracking-wide flex-shrink-0 mt-0.5 ${itemType === 'article' ? 'text-accent-secondary' : 'text-text-subtle'}`}>
                    {itemType === 'article' ? 'Article' : 'Entry'}
                  </span>
                  {hit.has_decision ? <span className="mt-0.5"><DecisionMark x_status={hit.x_status} /></span> : null}
                  <div className="flex-1 min-w-0">
                    <a href={permalinkHref(hit.id)}
                      onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: hit.id, itemType }))}
                      className="block text-sm text-text-base hover:text-accent-primary text-left line-clamp-1">
                      {hit.title}
                    </a>
                    {hit.one_line && <p className="text-xs text-text-muted line-clamp-1">{hit.one_line}</p>}
                  </div>
                  <span className="text-[10px] text-text-subtle flex-shrink-0 font-mono mt-0.5">{relTime(hit.created_at)}</span>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* Top Articles */}
      {topArticles.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Top Articles</h2>
          <ul className="space-y-1.5">
            {topArticles.slice(0, 10).map(a => (
              <li key={a.id} className="flex items-start gap-2">
                {a.sourced_count != null && a.sourced_count > 0 && (
                  <span className="text-[10px] text-accent-primary flex-shrink-0 mt-0.5 font-medium">×{a.sourced_count}</span>
                )}
                {a.has_decision ? <span className="mt-0.5"><DecisionMark x_status={a.x_status} /></span> : null}
                <div className="flex-1 min-w-0">
                  <a href={permalinkHref(a.id)}
                    onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: a.id, itemType: 'article' }))}
                    className="block text-sm text-text-base hover:text-accent-primary text-left line-clamp-1">
                    {a.title}
                  </a>
                  {a.one_line && <p className="text-xs text-text-muted line-clamp-1">{a.one_line}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Domain pulse */}
      <section>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Domain pulse</h2>
        {pulse.length === 0 ? (
          <p className="text-sm text-text-subtle">Loading…</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {pulse.map(p => (
              <li key={p.domain} className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full bg-domain-${p.domain}`} aria-hidden />
                <button type="button" onClick={() => navigate('explore')}
                  className="text-text-base hover:text-accent-primary capitalize text-left">
                  {p.domain}
                </button>
                <span className="flex-1 text-text-muted text-xs">{p.count} entries</span>
                <span className="text-text-subtle font-mono text-[10px]">{p.lastUpdated ? relTime(p.lastUpdated) : '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Needs review (下に移動) */}
      <section>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Needs review</h2>
        {needsReview.loading ? (
          <p className="text-sm text-text-subtle">Loading…</p>
        ) : (needsReview.stale.length === 0 && needsReview.reviewDue.length === 0 && needsReview.deprecated.length === 0 && !needsReview.pending) ? (
          <p className="text-sm text-text-muted">Nothing needs review right now.</p>
        ) : (
          <div className="space-y-3">
            {needsReview.stale.length > 0 && (
              <div>
                <h3 className="text-xs text-text-subtle mb-1">Stale</h3>
                <ul className="space-y-0.5 text-sm">
                  {needsReview.stale.map(a => (
                    <li key={a.id}>
                      <a href={permalinkHref(a.id)} className="text-text-base hover:text-accent-primary text-left"
                        onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: a.id, itemType: 'article' }))}>
                        {a.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {needsReview.reviewDue.length > 0 && (
              <div>
                <h3 className="text-xs text-text-subtle mb-1">Review due</h3>
                <ul className="space-y-0.5 text-sm">
                  {needsReview.reviewDue.map(a => (
                    <li key={a.id} className="flex items-center gap-2">
                      <a href={permalinkHref(a.id)} className="text-text-base hover:text-accent-primary text-left"
                        onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: a.id, itemType: 'article' }))}>
                        {a.title}
                      </a>
                      {a.review_by && (
                        <span className="text-[10px] text-state-warning whitespace-nowrap">due {a.review_by.slice(0, 10)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {needsReview.pending != null && needsReview.pending > 0 && (
              <div>
                <h3 className="text-xs text-text-subtle mb-1">Pending suggestions</h3>
                <button type="button" onClick={() => navigate('collaborate')} className="text-sm text-accent-primary hover:underline">
                  {needsReview.pending} pending → Review in Collaborate
                </button>
              </div>
            )}
            {needsReview.deprecated.length > 0 && (
              <div>
                <h3 className="text-xs text-text-subtle mb-1">Deprecated</h3>
                <ul className="space-y-0.5 text-sm">
                  {needsReview.deprecated.map(a => (
                    <li key={a.id}>
                      <a href={permalinkHref(a.id)} className="text-text-muted line-through hover:text-accent-primary text-left"
                        onClick={(e) => itemLinkClick(e, () => setSelectedItem({ id: a.id, itemType: 'article' }))}>
                        {a.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>
      </div>
    </div>
  )
}

export function HomeSidebar() {
  return null  // Home Sub-sidebar 廃止 (iter1 Phase 2)
}

function formatToday(): string {
  const d = new Date()
  return d.toISOString().slice(0, 10) + ' ' + ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]
}

function relTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    const sec = Math.floor(ms / 1000)
    if (sec < 3600) return `${Math.max(1, Math.floor(sec / 60))}m`
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`
    if (sec < 86400 * 30) return `${Math.floor(sec / 86400)}d`
    return new Date(iso).toISOString().slice(0, 10)
  } catch { return iso }
}
